/* eslint-disable operator-linebreak */
import Image from 'common/components/Image';
import { AppSyncFarm } from 'common/models/farm';
import { Nullable, Optional } from 'common/models/optional';
import { FarmUserInfo } from 'common/models/token';
import React from 'react';
import { animated, useSpring } from 'react-spring';
import { VaultModalEvent } from 'xstate/vaultModalEvent';
import {
  initialVaultModalContext,
  addLiquidityModalService,
} from 'xstate/vaultModalMachine';
import { FarmUtils } from 'utils/farmUtils';
import FormatNumber from '../FormatPercent/FormatNumber';
import SpinnerContainer from '../SpinnerContainer';
import WithdrawLiquidityModalContainer from './WithdrawLiquidityModalContainer';
import { ExternalLinkButton } from '../LinkButton/ExternalLinkButton';
import { NumberUtils } from 'utils/numberUtils';

type Props = {
  farm: Nullable<AppSyncFarm>;
  farmUserInfo: Optional<FarmUserInfo>;
  type: Optional<string>;
  refetchUserInfo: () => Promise<void>;
  updatePool: (poolId: number) => Promise<void>;
  loadingUserInfo: boolean;
  onDepositOpen: () => void;
};

const VaultAddLiquidityTab: React.FC<Props> = (props) => {
  // const [isDepositModalOpen, setIsDepositModalOpen] = React.useState(initialVaultModalContext.isDepositModalOpen);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = React.useState(
    initialVaultModalContext.isWithdrawModalOpen
  );

  React.useEffect(() => {
    const subscription = addLiquidityModalService.subscribe((state) => {
      // if (state.context.isDepositModalOpen !== isDepositModalOpen) {
      //   setIsDepositModalOpen(() => state.context.isDepositModalOpen);
      // }
      if (state.context.isWithdrawModalOpen !== isWithdrawModalOpen) {
        setIsWithdrawModalOpen(() => state.context.isWithdrawModalOpen);
      }
    });

    return subscription.unsubscribe;
    // Note that if isDepositModalOpen, isWithdrawModalOpen are not included in the array here
    // Then the function will not re-render
  }, [addLiquidityModalService, isWithdrawModalOpen]);

  const fadeInProps = useSpring({ opacity: !props.farm ? 0 : 1 });

  const openWithdrawModal = () => {
    addLiquidityModalService.send(VaultModalEvent.OPEN_WITHDRAW_MODAL);
  };

  const baseAppSyncToken = FarmUtils.getBaseToken(props.farm);
  const memberAppSyncToken = FarmUtils.getRewardToken(props.farm);

  const isHodlVault = FarmUtils.isHodlVault(props.farm);

  const baseToken =
    props.farmUserInfo &&
    props.farmUserInfo.tokens.filter(
      (t) => t.name === baseAppSyncToken?.name
    )[0];
  const rewardToken =
    props.farmUserInfo &&
    props.farmUserInfo.tokens.filter(
      (t) => t.name === memberAppSyncToken?.name
    )[0];

  const baseTokenIcon = FarmUtils.tokenIconSrc(baseToken?.name);
  const rewardTokenIcon = FarmUtils.tokenIconSrc(rewardToken?.name);

  // Angel vaults display liquidity in $ (and tokens), hodl vaults - only in tokens
  const totalLP = isHodlVault ? (
    <FormatNumber
      value={FarmUtils.amountInTokens(
        props.farmUserInfo?.totalLPUSD,
        baseAppSyncToken
      )}
      postfix={` ${baseAppSyncToken?.displayName}`}
      isLoading={false}
      displayDecimals={NumberUtils.determineDecimals(
        FarmUtils.amountInTokens(
          props.farmUserInfo?.totalLPUSD,
          baseAppSyncToken
        )
      )}
      useNumbro={true}
    />
  ) : (
    <FormatNumber
      value={props.farmUserInfo?.totalLPUSD}
      prefix="$"
      isLoading={false}
      useNumbro={true}
    />
  );

  const baseTokenAmount = isHodlVault ? (
    <FormatNumber
      value={FarmUtils.amountInTokens(
        baseToken?.totalAmountUSD,
        baseAppSyncToken
      )}
      postfix={` ${baseAppSyncToken?.displayName}`}
      isLoading={false}
      displayDecimals={NumberUtils.determineDecimals(
        FarmUtils.amountInTokens(baseToken?.totalAmountUSD, baseAppSyncToken)
      )}
      useNumbro={true}
    />
  ) : (
    <FormatNumber
      value={baseToken?.totalAmountUSD}
      prefix="$"
      isLoading={false}
      useNumbro={true}
    />
  );

  const rewardTokenAmount = isHodlVault ? (
    <FormatNumber
      value={FarmUtils.amountInTokens(
        rewardToken?.totalAmountUSD,
        memberAppSyncToken
      )}
      postfix={` ${memberAppSyncToken?.displayName}`}
      isLoading={false}
      displayDecimals={NumberUtils.determineDecimals(
        FarmUtils.amountInTokens(
          rewardToken?.totalAmountUSD,
          memberAppSyncToken
        )
      )}
      useNumbro={true}
    />
  ) : (
    <FormatNumber
      value={rewardToken?.totalAmountUSD}
      prefix="$"
      isLoading={false}
      useNumbro={true}
    />
  );

  return (
    <>
      <div className="flex flex-col p-4 primary-text-color">
        <div className="flex flex-row justify-between">
          <div className="value-text heading-color font-semibold">
            Your Value
          </div>
          <SpinnerContainer isLoading={props.loadingUserInfo}>
            <div className="value-text font-semibold">{totalLP}</div>
          </SpinnerContainer>
        </div>

        {props.farm && (
          <animated.div style={fadeInProps}>
            <div className="relative">
              <div className="flex flex-col">
                <div className="flex flex-row items-center justify-between mt-4">
                  <div className="flex flex-row items-center">
                    <Image
                      src={baseTokenIcon}
                      alt={`${baseToken?.name} icon`}
                      height="30px"
                      width="30px"
                      marginRight="5px"
                      marginLeft="5px"
                    />
                    <div className="value-text">{baseToken?.displayName}</div>
                  </div>
                  <div className="flex flex-col">
                    <div className="value-text">{baseTokenAmount}</div>
                    {!isHodlVault && (
                      <div className="sub-text text-align-right">
                        {baseToken?.totalAmount}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-row items-center justify-between mt-2">
                  <div className="flex flex-row items-center">
                    <Image
                      src={rewardTokenIcon}
                      alt={`${memberAppSyncToken?.name} icon`}
                      height="30px"
                      width="30px"
                      marginRight="5px"
                      marginLeft="5px"
                    />
                    <div className="value-text">
                      {memberAppSyncToken?.displayName}
                    </div>
                  </div>
                  <div className="flex-column">
                    <div className="value-text">{rewardTokenAmount}</div>
                    {!isHodlVault && (
                      <div className="sub-text text-align-right">
                        {rewardToken?.totalAmount}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </animated.div>
        )}
      </div>
      <div className="footer-bg-color flex justify-evenly items-center h-24 box-footer">
        <ExternalLinkButton
          to={props.farm?.extras.tradeUrl as string}
          disabled={!props.farm?.extras.tradeUrl}
        >
          Trade
        </ExternalLinkButton>
        <button
          className="primary-button-bg-color"
          type="button"
          onClick={props.onDepositOpen}
          data-dd-action-name="Add Liquidity Modal"
        >
          <span className="primary-button-color">Add Liquidity</span>
        </button>
        <button
          className="primary-button-bg-color"
          type="button"
          onClick={openWithdrawModal}
          data-dd-action-name="Open Withdraw Modal"
        >
          <span className="primary-button-color">Withdraw</span>
        </button>
      </div>
      {/* {isDepositModalOpen && (
        <AddLiquidityModalContainer
          farm={props.farm}
          farmUserInfo={props.farmUserInfo}
          farmUserInfoLoading={props.loadingUserInfo}
          refetchFarmUserInfo={props.refetchUserInfo}
          updatePool={props.updatePool}
          type={props.type}
          isShowing={isDepositModalOpen}
          toggle={() => addLiquidityModalService.send(VaultModalEvent.CLOSE_DEPOSIT_MODAL)}
        />
      )} */}
      {isWithdrawModalOpen && (
        <WithdrawLiquidityModalContainer
          farm={props.farm}
          farmUserInfo={props.farmUserInfo}
          farmUserInfoLoading={props.loadingUserInfo}
          refetchFarmUserInfo={props.refetchUserInfo}
          updatePool={props.updatePool}
          isShowing={isWithdrawModalOpen}
          toggle={() =>
            addLiquidityModalService.send(VaultModalEvent.CLOSE_WITHDRAW_MODAL)
          }
          type={props.type}
        />
      )}
    </>
  );
};

export default VaultAddLiquidityTab;
